import { useState, type ComponentType } from "react";
import { formatDistanceToNow } from "date-fns";
import { Mail, MessageSquare, PackageX, ShoppingCart, BellOff } from "lucide-react";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { useQueryClient } from "@tanstack/react-query";
import { notificationsApi } from "@shared/api/notifications";
import { SERVER_PREFIX, useNotificationStore, type AdminNotification, type AdminNotificationKind } from "../../src/stores/notificationStore";

const KIND_ICON: Record<AdminNotificationKind, ComponentType<{ className?: string }>> = {
  message: MessageSquare,
  "low-stock": PackageX,
  contact: Mail,
  order: ShoppingCart,
};

const KIND_STYLE: Record<AdminNotificationKind, string> = {
  message: "bg-blue-50 text-blue-500 dark:bg-blue-500/10",
  "low-stock": "bg-orange-50 text-orange-500 dark:bg-orange-500/10",
  contact: "bg-success-50 text-success-500 dark:bg-success-500/10",
  order: "bg-purple-50 text-purple-500 dark:bg-purple-500/10",
};

function NotificationRow({ notification, onNavigate }: { notification: AdminNotification; onNavigate: (n: AdminNotification) => void }) {
  const Icon = KIND_ICON[notification.kind];

  return (
    <li>
      <DropdownItem
        tag="a"
        href={notification.link}
        onItemClick={() => onNavigate(notification)}
        className={`flex gap-3 rounded-lg border-b border-gray-100 p-3 px-4.5 py-3 hover:bg-gray-100 dark:border-gray-800 dark:hover:bg-white/5 ${
          notification.read ? "opacity-60" : ""
        }`}
      >
        <span className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${KIND_STYLE[notification.kind]}`}>
          <Icon className="h-5 w-5" />
          {!notification.read && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-[1.5px] border-white bg-brand-500 dark:border-gray-900" />
          )}
        </span>

        <span className="block min-w-0 flex-1 text-left">
          <span className="mb-0.5 block truncate text-theme-sm font-medium text-gray-800 dark:text-white/90">
            {notification.title}
          </span>
          {notification.body && (
            <span className="mb-1 block truncate text-theme-xs text-gray-500 dark:text-gray-400">{notification.body}</span>
          )}
          <span className="text-theme-xs text-gray-400 dark:text-gray-500">
            {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
          </span>
        </span>
      </DropdownItem>
    </li>
  );
}

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const items = useNotificationStore((s) => s.items);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const markRead = useNotificationStore((s) => s.markRead);
  const markAllRead = useNotificationStore((s) => s.markAllRead);
  const clear = useNotificationStore((s) => s.clear);

  const queryClient = useQueryClient();

  const toggleDropdown = () => setIsOpen((v) => !v);
  const closeDropdown = () => setIsOpen(false);

  // Server-backed notifications (new orders, Stripe outcomes) keep their read state in NotificationService, so it survives a reload
  // and is shared by the whole team; tell it, then let the next poll confirm.
  const syncStaffRead = (all: boolean, id?: string) => {
    const call = all ? notificationsApi.readAllStaff() : id?.startsWith(SERVER_PREFIX) ? notificationsApi.markRead(id.slice(SERVER_PREFIX.length)) : null;
    call?.then(() => queryClient.invalidateQueries({ queryKey: ["notifications", "staff"] })).catch(() => {});
  };

  const handleNavigate = (notification: AdminNotification) => {
    markRead(notification.id);
    syncStaffRead(false, notification.id);
    closeDropdown();
  };
  const handleMarkAllRead = () => {
    markAllRead();
    syncStaffRead(true);
  };
  const handleClear = () => {
    clear();
    syncStaffRead(true); // otherwise the next poll would bring the same entries straight back
  };

  return (
    <div className="relative">
      <button
        className="relative dropdown-toggle flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        {unreadCount > 0 && (
          <span className="absolute right-0 top-0.5 z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-400 px-0.5 text-[10px] font-medium leading-none text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
        <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.75 2.29248C10.75 1.87827 10.4143 1.54248 10 1.54248C9.58583 1.54248 9.25004 1.87827 9.25004 2.29248V2.83613C6.08266 3.20733 3.62504 5.9004 3.62504 9.16748V14.4591H3.33337C2.91916 14.4591 2.58337 14.7949 2.58337 15.2091C2.58337 15.6234 2.91916 15.9591 3.33337 15.9591H4.37504H15.625H16.6667C17.0809 15.9591 17.4167 15.6234 17.4167 15.2091C17.4167 14.7949 17.0809 14.4591 16.6667 14.4591H16.375V9.16748C16.375 5.9004 13.9174 3.20733 10.75 2.83613V2.29248ZM14.875 14.4591V9.16748C14.875 6.47509 12.6924 4.29248 10 4.29248C7.30765 4.29248 5.12504 6.47509 5.12504 9.16748V14.4591H14.875ZM8.00004 17.7085C8.00004 18.1228 8.33583 18.4585 8.75004 18.4585H11.25C11.6643 18.4585 12 18.1228 12 17.7085C12 17.2943 11.6643 16.9585 11.25 16.9585H8.75004C8.33583 16.9585 8.00004 17.2943 8.00004 17.7085Z"
            fill="currentColor"
          />
        </svg>
      </button>
      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute -right-[240px] mt-[17px] flex h-[480px] w-[350px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark sm:w-[361px] lg:right-0"
      >
        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-gray-700">
          <h5 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Notifications</h5>
          {items.length > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-theme-xs font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
            >
              Mark all read
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500">
            <BellOff className="h-8 w-8" />
            <span className="text-theme-sm">No notifications yet</span>
          </div>
        ) : (
          <ul className="flex flex-col h-auto overflow-y-auto custom-scrollbar">
            {items.map((notification) => (
              <NotificationRow key={notification.id} notification={notification} onNavigate={handleNavigate} />
            ))}
          </ul>
        )}

        {items.length > 0 && (
          <button
            onClick={handleClear}
            className="block px-4 py-2 mt-3 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            Clear all
          </button>
        )}
      </Dropdown>
    </div>
  );
}
